import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import { clearVisaCountryDetails } from "./visacountrySlice";
import {
  createVisaCountry,
  getAllVisaCountries,
  getVisaCountryByID,
  updateVisaCountry,
} from "./services/visacountry.services";
import { getAllActiveDocument } from "../document/services/document.services";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { IDocument } from "../document/documentModel";

const CreateVisaCountry: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const [formKey, setFormKey] = useState(1);

  const VisaCountryDetail = useAppSelector(
    (state) => state.visacountry.VisaCountryDetail
  );

  const VisaCountryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.visacountry.formLoading);

  const DocumentList = useAppSelector((state) => state.document.DocumentList);

  useEffect(() => {
    dispatch(getAllActiveDocument());

    return () => {
      dispatch(clearVisaCountryDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (VisaCountryID) {
      dispatch(getVisaCountryByID(VisaCountryID));
    }
  }, [VisaCountryID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VisaCountryDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!VisaCountryID) {
        const insertPayload = {
          VisaCountry: values.VisaCountry,
          CurrencyCode: values.CurrencyCode,
          DocumentID: values?.DocumentID ? values?.DocumentID.join(",") : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createVisaCountry(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVisaCountryDetails());
          dispatch(getAllVisaCountries());
        }
      } else {
        const editPayload = {
          ID: VisaCountryID,
          VisaCountry: values.VisaCountry,
          CurrencyCode: values.CurrencyCode,
          DocumentID: values?.DocumentID ? values?.DocumentID.join(",") : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateVisaCountry(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVisaCountryDetails());
          dispatch(getAllVisaCountries());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={VisaCountryDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {VisaCountryID ? "Update Visa Country" : "Add Visa Country"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="VisaCountry"
                  label="Visa Country"
                  placeholder="i.e. india"
                  component={FormTextField}
                  astrike={true}
                  maxLength={50}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="CurrencyCode"
                  label="Currency Code"
                  placeholder="i.e IN"
                  component={FormTextField}
                  astrike={true}
                  type={"text"}
                  maxLength={10}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"DocumentID"}
                  name={"DocumentID"}
                  label={"Documents"}
                  component={FormMultiSelectionFiled}
                  options={DocumentList?.map((doc: IDocument) => {
                    return {
                      value: doc.ID,
                      label: doc.DocumentName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={VisaCountryID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVisaCountry;
